import React, {useState, useCallback, useEffect, useMemo} from 'react';
import debounce from 'lodash/debounce';
import {Layout, IconButton, CloseIcon, TextField} from '@phonectas/unifon-ui-kit';

const DEBOUNCE_TIMEOUT = 400;

interface SearchWidgetProps {
	searchQuery?: string;
	placeholder?: string;
	value: string;
	onChange: (query: string) => void;
	clearSearch: () => void;
	autoFocus?: boolean;
	fullWidth?: boolean;
}

const SearchWidget: React.FC<SearchWidgetProps> = ({
	searchQuery = '', 
	placeholder = '', 
	onChange, 
	clearSearch, 
	autoFocus = false,
	fullWidth = false,
}) => {
	const [query, setQuery] = useState(searchQuery);
	const [isTyping, setIsTyping] = useState(false);

	const debouncedSearch = useMemo(() => debounce(onChange, DEBOUNCE_TIMEOUT), [onChange]);

	const showReset = isTyping && query !== '';
	const resetSearch = useCallback(() => {
		clearSearch();
		setQuery('');
	}, [clearSearch]);

	const onQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const newQuery = event.target.value;
		if (query !== newQuery) {
			setQuery(newQuery);
			debouncedSearch(newQuery);
			setIsTyping(true);
		}
	}, [debouncedSearch, query]);

	const handlePaste = useCallback((event: React.ClipboardEvent<HTMLInputElement>) => {
		event.preventDefault();
		const paste = event.clipboardData.getData('text').trim();
		const newQuery = /^\d+$/.test(paste) ? paste.trim() : paste;

		setQuery(newQuery);
		debouncedSearch(newQuery);
		setIsTyping(true);

	}, [debouncedSearch]);

	const onBlurHandle = useCallback(() => {
		if (!query) {
			setIsTyping(false);
		}
	}, [query]);

	useEffect(() => {
		if (!isTyping) {
			setQuery(searchQuery);
			debouncedSearch(searchQuery);
		}
	}, [debouncedSearch, isTyping, searchQuery]);

	useEffect(() => {
		return () => debouncedSearch.cancel();
	}, [debouncedSearch]);

	return (
		<Layout fullWidth nowrap alignItems="center" style={{position: 'relative'}}>
			<Layout fullWidth direction='row'>
				<TextField
					value={query}
					compact
					onChange={onQueryChange}
					placeholder={placeholder}
					fullWidth={fullWidth}
					inputProps={{autoComplete: 'off'}}
					onBlur={onBlurHandle}
					autoFocus={autoFocus}
					onPaste={handlePaste}
				/>
				{
					showReset ? <IconButton
						style={{position: 'absolute', right: '0'}}
						onClick={resetSearch}
						compact color="secondary"
					>
						<CloseIcon />
					</IconButton> : null
				}
			</Layout>
		</Layout>
	);
}

export default React.memo(SearchWidget);

